import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

import MultipartFileUpload from '../Workflows/MultipartFileUpload';

const TreasuryPaymentUploadDialog = ({ open, onClose, onUploadComplete }) => {
  const [datasetType, setDatasetType] = useState('');
  const [bank, setBank] = useState('');
  const [banks, setBanks] = useState([]);

  // Fetch banks data
  useEffect(() => {
    const fetchBanks = async () => {
      try {
        const token = localStorage.getItem('token');
        const tenantId = localStorage.getItem('tenant_id');
        
        if (!token || !tenantId) {
          console.error('Authentication token or tenant ID not found');
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=ACTIVE`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch banks: ${response.status}`);
        }

        const banksData = await response.json();
        setBanks(banksData || []);
      } catch (error) {
        console.error('Error fetching banks:', error);
      }
    };

    if (open) {
      // Reset dropdowns when opening dialog
      setDatasetType('');
      setBank('');
      fetchBanks();
    }
  }, [open]);

  const handleCloseDialog = () => {
    onClose();
  };

  const handleUploadComplete = (results) => {
    // Add dropdown selections to results
    const enhancedResults = results.map(result => ({
      ...result,
      additional_details: {
        dataset_type: datasetType,
        bank: bank
      }
    }));
    
    if (onUploadComplete) {
      onUploadComplete(enhancedResults);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '95vh' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2 }}>
        <Box>
          <Typography variant="h6" component="div" sx={{ fontWeight: 500 }}>
            File Upload
          </Typography>
        </Box>
        <IconButton aria-label="close" onClick={handleCloseDialog}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 3 }}>
        {/* Upload Options Section */}
        <Box sx={{ mb: 3, p: 2, mx:2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#000000' }}>
            Select Upload Options
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {/* Dataset Type Dropdown */}
            <FormControl sx={{ minWidth: 100, flex: 1, mt: 1 }}>
              <InputLabel id="dataset-type-label" sx={{ 
                fontWeight: 500, 
                color: '#000000',
                fontSize: '0.75rem',
                transform: 'translate(12px, -4px) scale(0.85)',
                backgroundColor: '#f8f9fa',
                padding: '0 2px'
              }}>
                Dataset Type
              </InputLabel>
              <Select
                labelId="dataset-type-label"
                value={datasetType}
                onChange={(e) => setDatasetType(e.target.value)}
                displayEmpty
                sx={{ 
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d0d0d0',
                  }
                }}
              >
                <MenuItem value="">
                  <em>Select Dataset Type</em>
                </MenuItem>
                <MenuItem value="Invoice">
                  Invoice
                </MenuItem>
                <MenuItem value="Payroll">
                  Payroll
                </MenuItem>
              </Select>
            </FormControl>

            {/* Bank Dropdown */}
            <FormControl sx={{ minWidth: 100, flex: 1, mt: 1 }}>
              <InputLabel id="bank-label" sx={{ 
                fontWeight: 500, 
                color: '#000000',
                fontSize: '0.75rem',
                transform: 'translate(12px, -4px) scale(0.85)',
                backgroundColor: '#f8f9fa',
                padding: '0 2px'
              }}>
                Bank
              </InputLabel>
              <Select
                labelId="bank-label"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                displayEmpty
                sx={{ 
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d0d0d0',
                  }
                }}
              >
                <MenuItem value="">
                  <em>Select Bank</em>
                </MenuItem>
                {banks.map((bank) => (
                  <MenuItem key={bank.bankId} value={bank.bankName}>
                    {bank.bankName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* File Upload Component */}
        <Box sx={{ mt: 2 }}>
          <MultipartFileUpload 
            onClose={handleCloseDialog}
            onUploadComplete={handleUploadComplete}
            category="TR"
            additionalDetails={{
              dataset_type: datasetType,
              bank: bank
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TreasuryPaymentUploadDialog;
